import React, { useMemo, useEffect, useRef } from 'react';
import TabBarIcon from '../TabBarIcon';
import { COURSE_TYPES, FINISH_OPTIONS } from '../../utils/constants';
import { generateSequenceString, parseSequenceDescription, validateAndAdjustConfig } from '../../utils/courseSequence';
import '../../styles/CourseForm.css';

const CourseConfiguration = ({ courseData, setCourseData, offset, setOffset }) => {
  // Ref to prevent infinite validation loops
  const isValidatingRef = useRef(false);

  // Validate and fix state on mount and when course type changes
  useEffect(() => {
    if (!courseData?.courseType || isValidatingRef.current) return;

    const configToValidate = {
      ...courseData,
      offsetEnabled: offset?.enabled,
      gates: [
        ...(courseData.gate1 ? ['1'] : []),
        ...(courseData.gate3 ? ['3'] : []),
        ...(courseData.gate4 ? ['4'] : [])
      ]
    };

    try {
      const validation = validateAndAdjustConfig(configToValidate);
      
      if (validation.adjustments.length > 0 && !validation.isValid) {
        isValidatingRef.current = true; // Prevent re-validation
        console.log('🔧 Auto-fixing invalid state on mount/change:', validation.adjustments);
        
        // Apply the adjustments to fix the actual state
        setCourseData(prev => {
          const fixedData = { ...prev };
          
          // Update finish type if needed
          if (validation.adjustedConfig.finishType !== fixedData.finishType) {
            fixedData.finishType = validation.adjustedConfig.finishType;
          }
          
          // Update gate configurations to match validation
          const adjustedGates = validation.adjustedConfig.gates || [];
          fixedData.gate1 = adjustedGates.includes('1');
          fixedData.gate3 = adjustedGates.includes('3'); 
          fixedData.gate4 = adjustedGates.includes('4');
          
          return fixedData;
        });

        // Update offset if needed
        if (validation.adjustedConfig.offsetEnabled !== offset?.enabled) {
          setOffset(prev => ({
            ...prev,
            enabled: validation.adjustedConfig.offsetEnabled
          }));
        }
        
        // Reset validation flag after a short delay
        setTimeout(() => {
          isValidatingRef.current = false;
        }, 100);
      }
    } catch (error) {
      console.error('State validation failed:', error);
    }
  }, [courseData?.courseType]); // Only depend on course type changes

  // Memoize sequence generation to prevent infinite re-renders
  const courseSequence = useMemo(() => {
    const sequenceData = { 
      ...courseData, 
      offsetEnabled: offset?.enabled,
      gates: [
        ...(courseData.gate1 ? ['1'] : []),
        ...(courseData.gate3 ? ['3'] : []),
        ...(courseData.gate4 ? ['4'] : [])
      ],
      offsets: (offset?.enabled && courseData.gate1 === false) ? ['1'] : []
    };

    try {
      return generateSequenceString(sequenceData);
    } catch (error) {
      console.error('Sequence generation failed:', error.message);
      return 'Configuration Error';
    }
  }, [courseData, offset?.enabled]);

  const handleInputChange = (field, value) => {
    applyCourseTypeChange(field, value);
  };

  const applyCourseTypeChange = (field, value) => {
    setCourseData(prev => {
      const newData = { ...prev, [field]: value };
      
      // If course type changes, automatically validate and adjust configuration
      if (field === 'courseType') {
        try {
          // Create the configuration object with the new course type
          const configToValidate = {
            ...newData,
            offsetEnabled: offset?.enabled,
            gates: [
              ...(prev.gate1 ? ['1'] : []),
              ...(prev.gate3 ? ['3'] : []),
              ...(prev.gate4 ? ['4'] : [])
            ]
          };
          
          // Validate and get adjusted configuration
          const validation = validateAndAdjustConfig(configToValidate);
          
          if (validation.adjustments.length > 0) {
            console.log('🔧 Auto-adjustments for course type change:', validation.adjustments);
            
            // Apply the adjustments to newData
            if (validation.adjustedConfig.finishType !== newData.finishType) {
              newData.finishType = validation.adjustedConfig.finishType;
            }
            
            // Update gate configurations based on validation
            const adjustedGates = validation.adjustedConfig.gates || [];
            newData.gate1 = adjustedGates.includes('1');
            newData.gate3 = adjustedGates.includes('3'); 
            newData.gate4 = adjustedGates.includes('4');
            
            // Update offset if needed
            if (validation.adjustedConfig.offsetEnabled !== offset?.enabled) {
              setOffset(prev => ({
                ...prev,
                enabled: validation.adjustedConfig.offsetEnabled
              }));
            }
          }
        } catch (error) {
          console.error('Validation failed during course type change:', error);
          // Fallback to original behavior
          const availableFinishes = FINISH_OPTIONS[value] || [];
          if (availableFinishes.length > 0) {
            newData.finishType = availableFinishes[0].value;
          }
        }
      }
      
      return newData;
    });
  };

  const handleOffsetChange = (field, value) => {
    setOffset(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if current finish type requires distance/angle settings
  const requiresFinishDistance = (finishType) => {
    return finishType && (
      finishType.includes('Reach') ||
      finishType.includes('Windward') ||
      finishType.includes('separate') ||
      finishType !== 'Start finish together'
    );
  };

  const requiresFinishAngle = (finishType) => {
    return finishType && (
      finishType.includes('Reach') ||
      finishType.includes('Windward')
    );
  };

  const currentFinishOptions = FINISH_OPTIONS[courseData.courseType] || [];

  // Helper function to check if we should show gates based on course type
  const shouldShowGates = () => {
    return courseData.courseType === COURSE_TYPES.T_LEEWARD || 
           courseData.courseType === COURSE_TYPES.T_WINDWARD ||
           courseData.courseType === COURSE_TYPES.T_INNER_TRAPEZOID ||
           courseData.courseType === COURSE_TYPES.T_OUTER_TRAPEZOID;
  };

  // Helper function to check if we should show Gate 3 (only for trapezoids)
  const shouldShowGate3 = () => {
    return courseData.courseType === COURSE_TYPES.T_INNER_TRAPEZOID ||
           courseData.courseType === COURSE_TYPES.T_OUTER_TRAPEZOID;
  };

  // Helper function to check if we should show Spinnaker option (only for trapezoids)
  const shouldShowSpinnaker = () => {
    return courseData.courseType === COURSE_TYPES.T_INNER_TRAPEZOID ||
           courseData.courseType === COURSE_TYPES.T_OUTER_TRAPEZOID;
  };

  return (
    <div className="course-configuration-form">
      {/* Course Sequence Visualization */}
      <div className="config-section">
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: '8px' 
        }}>
          <TabBarIcon name="route" color="#2196f3" />
          <span>Course Sequence:</span>
          <span style={{ 
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#495057',
            backgroundColor: '#f8f9fa',
            padding: '4px 8px',
            border: '1px solid #e9ecef',
            borderRadius: '4px'
          }}>
            {courseSequence}
          </span>
        </h3>

        <div style={{ 
          padding: '10px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px', 
          fontSize: '12px', 
          color: '#856404',
          marginTop: '5px',
          marginBottom: '20px'
        }}>
          <strong>Sequence Legend:</strong> S=Start, Numbers=Marks, G=Gate, p/s=Port/Starboard rounding, F=Finish
        </div>
      </div>

      <div className="config-section">
        <h3>
          <TabBarIcon name="ship" color="#2196f3" />
          <span>Fleet Configuration</span>
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>Number of Boats</label>
            <input
              type="number"
              min="1"
              max="100"
              value={courseData.numberOfBoats}
              onChange={(e) => handleInputChange('numberOfBoats', parseInt(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Average Boat Length (m)</label>
            <input
              type="number"
              min="1"
              max="50"
              step="0.1"
              value={courseData.averageBoatLength}
              onChange={(e) => handleInputChange('averageBoatLength', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Start Line Scale Factor</label>
            <input
              type="number"
              min="1"
              max="3"
              step="0.1"
              value={courseData.startLineScaleFactor}
              onChange={(e) => handleInputChange('startLineScaleFactor', parseFloat(e.target.value) || 1)}
            />
          </div>
        </div>
      </div>

      <div className="config-section">
        <h3>
          <TabBarIcon name="wind" color="#2196f3" />
          <span>Wind & Course</span>
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>Wind Direction (°)</label>
            <input
              type="number"
              min="0"
              max="360"
              value={courseData.windDirection}
              onChange={(e) => handleInputChange('windDirection', parseInt(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Course Distance (m)</label>
            <input
              type="number"
              min="0.1"
              max="500000"
              step="0.1"
              value={courseData.distance}
              onChange={(e) => handleInputChange('distance', parseFloat(e.target.value) || 0)}
            />
          </div>

          {/* Triangle-specific: Angle to Mark 2 */}
          {courseData.courseType === COURSE_TYPES.T_TRIANGLE && (
            <div className="form-group">
              <label>Angle to Mark 2 (°)</label>
              <input
                type="number"
                min="1"
                max="179"
                value={courseData.triangleAngle || 60}
                onChange={(e) => handleInputChange('triangleAngle', parseInt(e.target.value) || 60)}
              />
              <div className="form-hint">Angle from start to Mark 2 (same used for Mark 4)</div>
            </div>
          )}
          
          <div className="form-group">
            <label>Course Type</label>
            <select
              value={courseData.courseType}
              onChange={(e) => handleInputChange('courseType', e.target.value)}
            >
              {Object.entries(COURSE_TYPES).map(([key, value]) => (
                <option key={key} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Finish Type Selection */}
        <div className="form-row">
          <div className="form-group">
            <label>Finish Type</label>
            <select
              value={courseData.finishType || ''}
              onChange={(e) => handleInputChange('finishType', e.target.value)}
            >
              {currentFinishOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Conditional Finish Distance */}
          {requiresFinishDistance(courseData.finishType) && (
            <div className="form-group">
              <label>Finish Distance (M)</label>
              <input
                type="number"
                min="0.01"
                max="2"
                step="0.01"
                value={courseData.finishDistance || 0.05}
                onChange={(e) => handleInputChange('finishDistance', parseFloat(e.target.value) || 0.05)}
                />
              <div className="form-hint">Distance from last mark to finish line</div>
            </div>
          )}

          {/* Conditional Finish Angle */}
          {requiresFinishAngle(courseData.finishType) && (
            <div className="form-group">
              <label>Finish Angle (°)</label>
              <input
                type="number"
                min="0"
                max="360"
                value={courseData.finishAngle || 70}
                onChange={(e) => handleInputChange('finishAngle', parseInt(e.target.value) || 70)}
                />
              <div className="form-hint">Angle to finish line from last mark</div>
            </div>
          )}
        </div>
      </div>

      <div className="config-section">
        <h3>
          <TabBarIcon name="flag" color="#2196f3" />
          <span>Course Marks</span>
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>Leeward Distance (M)</label>
            <input
              type="number"
              min="0.1"
              max="5"
              step="0.1"
              value={courseData.leewardDistance}
              onChange={(e) => handleInputChange('leewardDistance', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Windward Beats</label>
            <input
              type="number"
              min="1"
              max="4"
              value={courseData.windwardBeats}
              onChange={(e) => handleInputChange('windwardBeats', parseInt(e.target.value) || 1)}
            />
          </div>
          
          <div className="form-group">
            <label>Gate Width (# Avg boat lengths)</label>
            <input
              type="number"
              min="3"
              max="12"
              value={courseData.gateWidth}
              onChange={(e) => handleInputChange('gateWidth', parseInt(e.target.value) || 100)}
            />
          </div>
        </div>

        {/* Gate Configuration */}
        {shouldShowGates() && (
          <>
            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={courseData.gate1 !== false} // Default enabled
                    onChange={(e) => handleInputChange('gate1', e.target.checked)}
                        />
                  <span>Gate 1 [1S/1P]</span>
                </label>
                <div className="form-hint">Split windward mark into port/starboard gates</div>
              </div>
              
              {shouldShowGate3() && (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={courseData.gate3 !== false} // Default enabled
                      onChange={(e) => handleInputChange('gate3', e.target.checked)}
                    />
                    <span>Gate 3 [3S/3P]</span>
                  </label>
                  <div className="form-hint">Split middle windward mark into port/starboard gates</div>
                </div>
              )}
              
              {/* Triangle-specific: Gate at Mark 4 */}
              {courseData.courseType === COURSE_TYPES.T_TRIANGLE ? (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={courseData.gate4 !== false}
                      onChange={(e) => handleInputChange('gate4', e.target.checked)}
                    />
                    <span>Gate at Mark 4</span>
                  </label>
                  <div className="form-hint">Enable a gate at Mark 4 (triangle courses only)</div>
                </div>
              ) : (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={courseData.gate4 !== false} // Default enabled
                      onChange={(e) => handleInputChange('gate4', e.target.checked)}
                    />
                    <span>Gate 4 [4S/4P]</span>
                  </label>
                  <div className="form-hint">Split leeward mark into port/starboard gates</div>
                </div>
              )}
            </div>

            {/* Spinnaker Configuration - Only for trapezoid courses */}
            {shouldShowSpinnaker() && (
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={courseData.spinnaker || false} // Default disabled
                      onChange={(e) => handleInputChange('spinnaker', e.target.checked)}
                    />
                    <span>Spinnaker Course (60°/120° angles)</span>
                  </label>
                  <div className="form-hint">
                    {courseData.spinnaker 
                      ? 'Using spinnaker-friendly angles (60°/120°)' 
                      : 'Using no-spinnaker angles (70°/110°)'
                    }
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="config-section">
        <h3>
          <TabBarIcon name="adjust" color="#2196f3" />
          <span>Offset Configuration</span>
        </h3>
        
        <div className="form-row">
          {/* Only show Enable Offset if Gate 1 is disabled */}
          {courseData.gate1 === false && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={offset.enabled}
                  onChange={(e) => handleOffsetChange('enabled', e.target.checked)}
                    />
                <span>Enable Offset</span>
              </label>
              <div className="form-hint">Add an offset mark for windward mark rounding</div>
            </div>
          )}
          
          {/* Show a message when Gate 1 is enabled explaining why offset is not available */}
          {courseData.gate1 !== false && (
            <div className="form-group">
              <div style={{ padding: '10px', background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px' }}>
                <strong>Offset Not Available</strong><br/>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Offset mark is not used when Gate 1 is enabled. Disable Gate 1 to use offset mark.
                </span>
              </div>
            </div>
          )}
          
          {/* Show offset distance and angle inputs only if offset is enabled and Gate 1 is disabled */}
          {offset.enabled && courseData.gate1 === false && (
            <>
              <div className="form-group">
                <label>Offset Distance (M)</label>
                <input
                  type="number"
                  min="0.01"
                  max="1"
                  step="0.01"
                  value={offset.distance}
                  onChange={(e) => handleOffsetChange('distance', parseFloat(e.target.value) || 0)}
                />
                <div className="form-hint">Distance from windward mark to offset mark</div>
              </div>
              
              <div className="form-group">
                <label>Offset Angle (°)</label>
                <input
                  type="number"
                  min="0"
                  max="360"
                  value={offset.angle}
                  onChange={(e) => handleOffsetChange('angle', parseInt(e.target.value) || 0)}
                />
                <div className="form-hint">Bearing from windward mark to offset mark</div>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default CourseConfiguration;